import { FormattedMessage, useIntl } from 'react-intl'

import { Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { useIsMutating, useMutation } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useState } from 'react'
import {
  InitiatePaymentRequestDTO,
  InitiatePaymentResponseDTO,
  ParkingEvent,
  PaymentLanguage,
} from '../../../apis/webPayment/dto'
import paymentsApi from '../../../apis/webPayment/paymentsApi'
import testidKeys from '../../../app/testidKeys'
import useLocale from '../../../hooks/useLocale'
import useUserEmail from '../../../hooks/useUserEmail'
import { MoovyToast } from '../../../utils/moovyToast'
import CardInvoice from './cardInvoice'

export type StepInvoiceProps = {
  licensePlate: string
  invoices: ParkingEvent[]
}

const initiatePaymentMutationKey = ['initiatePayment']

const StepInvoice = ({ licensePlate, invoices }: StepInvoiceProps) => {
  const intl = useIntl()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { locale } = useLocale()
  const [email] = useUserEmail()

  const queryInitiatePayment = useMutation({
    mutationKey: initiatePaymentMutationKey,
    mutationFn: (request: InitiatePaymentRequestDTO) =>
      paymentsApi.createPaymentSession(request),
    onError: (_error) => {
      MoovyToast.showToast(
        intl.formatMessage({
          id: 'page.webPayment.stepInvoice.error.initiatePayment',
        }),
        'error',
      )
    },
    onSuccess: (response: AxiosResponse<InitiatePaymentResponseDTO>) => {
      if (response.data.href) {
        setIsRedirecting(true)
        window.location.href = response.data.href
      }
    },
  })

  const isInitiatePaymentMutating = useIsMutating({
    mutationKey: initiatePaymentMutationKey,
  })

  const GridInvoice = () => {
    return (
      <Grid
        container
        sx={{
          flexGrow: 1,
          justifyContent: 'center',
        }}
        spacing={2}
        data-testid={testidKeys.STEP_INVOICE_INVOICE_CONTAINER}
      >
        {invoices.map((item: ParkingEvent) => (
          <Grid
            size={{ sm: 12, md: 6 }}
            key={`invoiceContainer-${item.invoiceNumber}`}
          >
            <CardInvoice
              key={`invoiceCard-${item.invoiceNumber}`}
              invoiceNumber={item.invoiceNumber}
              locationName={intl.formatMessage({ id: 'facility.name' })}
              startTimeISO={item.startTimeISO}
              duration={item.duration}
              price={item.price}
              vat={item.vat}
              vatPrice={item.vatPrice}
              isButtonDisabled={!!(isRedirecting || isInitiatePaymentMutating)}
              onInvoiceClick={(userEmail) => {
                const languages: PaymentLanguage[] = ['FI', 'EN', 'SV']
                queryInitiatePayment.mutate({
                  language:
                    languages.find((lang) => lang === locale.toUpperCase()) ??
                    'EN',
                  invoiceNumber: item.invoiceNumber,
                  email: userEmail,
                })
              }}
            />
          </Grid>
        ))}
      </Grid>
    )
  }

  return (
    <>
      <Grid size={12} style={{ padding: 50 }}>
        <Typography variant="h3">
          <FormattedMessage id="page.webPayment.stepInvoice.info.invoice" />
        </Typography>
      </Grid>
      <Grid
        size={12}
        style={{ paddingLeft: 20, paddingRight: 20, paddingBottom: 20 }}
      >
        <Typography>
          <FormattedMessage
            id="page.webPayment.stepInvoice.info.invoiceDescription"
            values={{
              count: invoices.length,
              licensePlate,
            }}
          />
        </Typography>
      </Grid>
      <GridInvoice />
    </>
  )
}

export default StepInvoice
